import api, { transformRequest, transformResponse } from "services/api";

export const authApi = api.enhanceEndpoints({ addTagTypes: ["Me", "MfaDevices"] }).injectEndpoints({
  endpoints: (build) => ({
    getDebtorPortalConfigurations: build.query({
      query: (payload) => `/api/v1/consumer/portal-configurations/${payload.agencySlug}/debtor/`,
      transformResponse: (response) => transformResponse(response),
    }),
    changePassword: build.mutation({
      query: (payload) => ({
        url: "/api/v1/common/users/me/change-password/",
        method: "POST",
        body: transformRequest(payload),
      }),
      transformResponse: (response) => transformResponse(response),
    }),
    creditorUserSignUp: build.mutation({
      query: (payload) => ({
        url: "/api/v1/creditor/user/register/",
        method: "POST",
        body: transformRequest(payload),
      }),
      transformResponse: (response) => transformResponse(response),
    }),
    fetchMe: build.query({
      query: () => ({
        url: "/api/v1/common/users/me/",
        method: "GET",
        headers: {
          "aktos-suppress-error": "true",
        },
      }),
      transformResponse: (response) => transformResponse(response),
      providesTags: [{ type: "Me" }],
    }),
    login: build.mutation({
      query: (payload) => ({
        url: "/api/v1/auth/login/",
        method: "POST",
        body: transformRequest(payload),
      }),
      transformResponse: (response) => transformResponse(response),
    }),
    logout: build.mutation({
      query: () => ({
        url: "/api/v1/auth/logout/",
        method: "POST",
      }),
      transformResponse: (response) => transformResponse(response),
    }),
    refreshToken: build.mutation({
      query: (payload) => ({
        url: "/api/v1/auth/login/refresh/",
        method: "POST",
        body: transformRequest(payload),
      }),
      transformResponse: (response) => transformResponse(response),
    }),
    consumerUserSignUp: build.mutation({
      query: (payload) => ({
        url: "/api/v1/consumer/auth/register/",
        method: "POST",
        body: transformRequest(payload),
      }),
      transformResponse: (response) => transformResponse(response),
    }),
    consumerAccountLookup: build.mutation({
      query: (payload) => ({
        url: `/api/v1/consumer/auth/lookup/`,
        method: "POST",
        body: transformRequest(payload),
      }),
      transformResponse: (response) => transformResponse(response),
    }),
    consumerForgotPassword: build.mutation({
      query: (payload) => ({
        url: `/api/v1/consumer/auth/forgot-password/`,
        method: "POST",
        body: transformRequest(payload),
      }),
    }),
    consumerPasswordReset: build.mutation({
      query: (payload) => ({
        url: `/api/v1/consumer/auth/password-reset/`,
        method: "POST",
        body: transformRequest(payload),
      }),
    }),
    listLoginMethods: build.mutation({
      query: (payload) => ({
        url: "/api/v1/auth/login/methods/",
        method: "POST",
        body: transformRequest(payload),
      }),
      transformResponse: (response) => transformResponse(response),
    }),
    ssoExchange: build.mutation({
      query: (payload) => ({
        url: "/api/v1/auth/sso/token-exchange/",
        method: "POST",
        body: transformRequest(payload),
      }),
      transformResponse: (response) => transformResponse(response),
    }),
    verifyMfaCode: build.mutation({
      query: (payload) => ({
        url: `/api/v1/auth/mfa-verification/`,
        method: "POST",
        body: transformRequest(payload),
      }),
      invalidatesTags: [{ type: "MfaDevices", id: "LIST" }, { type: "Me" }],
    }),
    fetchAllMfaDevices: build.query({
      query: () => `/api/v1/auth/mfa/`,
      transformResponse: (response) => transformResponse(response),
      providesTags: (results) =>
        results
          ? // successful query
            [
              ...results.map(({ id }) => ({ type: "MfaDevices", id })),
              { type: "MfaDevices", id: "LIST" },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Employers', id: 'LIST' }` is invalidated
            [{ type: "MfaDevices", id: "LIST" }],
    }),
    createMfaDevice: build.mutation({
      query: (payload) => ({
        url: `/api/v1/auth/mfa/create/`,
        method: "POST",
        body: transformRequest(payload),
      }),
      transformResponse: (response) => transformResponse(response),
      invalidatesTags: [{ type: "MfaDevices", id: "LIST" }],
    }),
    disableMfaDevice: build.mutation({
      query: (payload) => ({
        url: `/api/v1/auth/mfa/delete/`,
        method: "POST",
        body: transformRequest(payload),
      }),
      invalidatesTags: [{ type: "MfaDevices", id: "LIST" }],
    }),
    sendMfaToken: build.mutation({
      query: (payload) => ({
        url: `/api/v1/auth/mfa/send-token/`,
        method: "POST",
        body: transformRequest(payload),
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDebtorPortalConfigurationsQuery,
  useChangePasswordMutation,
  useFetchMeQuery,
  useLazyFetchMeQuery,
  useLoginMutation,
  useLogoutMutation,
  useRefreshTokenMutation,
  useCreditorUserSignUpMutation,
  useConsumerAccountLookupMutation,
  useConsumerUserSignUpMutation,
  useConsumerForgotPasswordMutation,
  useConsumerPasswordResetMutation,
  useListLoginMethodsMutation,
  useSsoExchangeMutation,
  useVerifyMfaCodeMutation,
  useFetchAllMfaDevicesQuery,
  useCreateMfaDeviceMutation,
  useDisableMfaDeviceMutation,
  useSendMfaTokenMutation,
} = authApi;
